import { Badge, Button, Card, cn, type BadgeTone } from '@/components/ui'
import type { MatterDetail, PipelineRun } from '@/api/types'
import { useRouter } from '@/routes'

import { nextStep, progressSteps, type ProgressState } from './nextStep'

/**
 * The first thing on the Case screen, and the only one written for an attorney.
 *
 * Everything below this card is a record of what the pipeline did, and it is
 * kept — a refusal nobody can inspect is how the first live draft went out over
 * a blocking deadline. But a record is not an instruction, and reading ten
 * cards of stage names to work out whether you are waiting on the client, on a
 * date, or on nothing at all is the reported failure this answers.
 *
 * Two things, in this order: **how far the matter got**, and **what to do
 * next**. Both are derived in `nextStep.ts` from values the server already
 * decided, so this file only draws them.
 */

const TONE_SURFACE: Record<BadgeTone, string> = {
  ok: 'border-ok-line bg-ok-bg',
  warn: 'border-warn-line bg-warn-bg',
  block: 'border-block-line bg-block-bg',
  accent: 'border-brand-200 bg-brand-100',
  neutral: 'border-divider bg-ground',
  outline: 'border-divider bg-surface',
}

const TONE_TEXT: Record<BadgeTone, string> = {
  ok: 'text-ok-fg',
  warn: 'text-warn-fg',
  block: 'text-block-fg',
  accent: 'text-brand-700',
  neutral: 'text-ink',
  outline: 'text-ink',
}

const STEP_DOT: Record<ProgressState, string> = {
  done: 'border-brand bg-brand',
  blocked: 'border-block-line bg-block-fg',
  todo: 'border-divider bg-surface',
}

const STEP_TEXT: Record<ProgressState, string> = {
  done: 'text-ink',
  blocked: 'text-block-fg font-semibold',
  todo: 'text-muted',
}

/**
 * Five steps, left to right, with the one that stopped marked.
 *
 * Drawn as a list rather than a row of divs so a screen reader reads it as the
 * sequence it is, and each step carries its state in text — `aria-label` — and
 * not only in the colour of a dot.
 */
function ProgressRail({
  run,
  matter,
}: {
  run: PipelineRun | null | undefined
  matter: MatterDetail | undefined
}) {
  const steps = progressSteps(run, matter)

  return (
    <ol
      aria-label="How far this matter has got"
      className="m-0 flex list-none flex-wrap gap-x-1 gap-y-2 p-0"
    >
      {steps.map((step, index) => (
        <li
          key={step.label}
          className="flex min-w-0 flex-1 basis-[140px] flex-col gap-1"
          title={step.hint}
        >
          <div className="flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className={cn('h-2.5 w-2.5 shrink-0 rounded-full border', STEP_DOT[step.state])}
            />
            {index < steps.length - 1 ? (
              <span aria-hidden="true" className="h-px min-w-2 flex-1 bg-divider" />
            ) : null}
          </div>
          <span className={cn('text-micro leading-tight', STEP_TEXT[step.state])}>
            {step.label}
          </span>
          {/* The state in words, for anyone not reading the colour. */}
          <span className="sr-only">
            {step.state === 'done'
              ? 'done'
              : step.state === 'blocked'
                ? 'stopped here'
                : 'not reached'}
          </span>
        </li>
      ))}
    </ol>
  )
}

export function NextStepCard({
  run,
  matter,
}: {
  run: PipelineRun | null | undefined
  matter: MatterDetail | undefined
}) {
  const router = useRouter()
  const step = nextStep(run, matter)

  function onAct() {
    const action = step.action
    if (!action) return
    if (action.navigateTo) {
      router.navigate(action.navigateTo)
      return
    }
    if (action.scrollTo) {
      const target = document.getElementById(action.scrollTo)
      // `scrollIntoView` is not implemented in jsdom, and focus is what actually
      // matters for a keyboard user — the scroll is the sighted convenience.
      target?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
      target?.focus?.({ preventScroll: true })
    }
  }

  return (
    <Card
      role="group"
      aria-label="What to do next"
      className={cn('gap-4 border', TONE_SURFACE[step.tone])}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="card-kicker">Where this stands</div>
          <h3 className={cn('m-0 text-[17px] leading-snug', TONE_TEXT[step.tone])}>
            {step.headline}
          </h3>
        </div>
        {run?.exportable ? <Badge tone="ok">ready to send</Badge> : null}
      </div>

      <p className="m-0 max-w-[70ch] text-body text-ink">{step.detail}</p>

      {step.action ? (
        <div>
          <Button type="button" variant="primary" onClick={onAct}>
            {step.action.label}
          </Button>
        </div>
      ) : null}

      <div className="border-t border-divider pt-3">
        <ProgressRail run={run} matter={matter} />
      </div>
    </Card>
  )
}
